"use client";

import type { ClerkAPIResponseError } from "@clerk/shared/error";
import type { OAuthStrategy } from "@clerk/shared/types";
import { useSignIn } from "@klarify/auth/client";
import { getLocalizedPathname } from "@klarify/i18n/path";
import { Spinner } from "@klarify/ui/components/spinner";
import { Button } from "@klarify/ui/components/ui/button";
import { Input } from "@klarify/ui/components/ui/input";
import { Label } from "@klarify/ui/components/ui/label";
import { Trans, useLingui } from "@lingui/react/macro";
import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import GoogleGIcon from "../svg/google-g-icon";
import { ForgotPassword } from "./forgot-password";
import { TwoFactorVerification } from "./two-factor-verification";

interface CustomSignInProps {
	signUpUrl?: string;
	forceRedirectUrl?: string;
	redirectUrl?: string;
	initialEmail?: string;
	onRedirectStart?: () => void;
	onTwoFactorStateChange?: (needsTwoFactor: boolean) => void;
	/** Resolves once the role/time cookies are written; awaited before signIn.create. */
	awaitCookies: () => Promise<void>;
	/** Re-runs the cookie writes; awaited before OAuth so an expired redirect-time cookie is rewritten. */
	refreshCookies: () => Promise<void>;
}

export function CustomSignIn({
	signUpUrl,
	forceRedirectUrl,
	redirectUrl,
	initialEmail,
	onRedirectStart,
	onTwoFactorStateChange,
	awaitCookies,
	refreshCookies,
}: CustomSignInProps) {
	const { t } = useLingui();
	const { isLoaded, signIn, setActive } = useSignIn();
	const [emailAddress, setEmailAddress] = useState(initialEmail ?? "");
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [emailError, setEmailError] = useState("");
	const [passwordError, setPasswordError] = useState("");
	const [generalError, setGeneralError] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isRedirecting, setIsRedirecting] = useState(false);
	const [showForgotPassword, setShowForgotPassword] = useState(false);
	const [needsTwoFactor, setNeedsTwoFactor] = useState(false);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!signIn) {
			return;
		}

		setIsSubmitting(true);
		setEmailError("");
		setPasswordError("");
		setGeneralError("");

		try {
			// Cookies drive /auth/redirect role + is-new-user routing; must land first.
			await awaitCookies();
			const result = await signIn.create({
				identifier: emailAddress,
				password,
			});

			if (result.status === "complete") {
				setIsRedirecting(true);
				onRedirectStart?.();
				await setActive({ session: result.createdSessionId });
				window.location.href =
					forceRedirectUrl ?? redirectUrl ?? getLocalizedPathname("/");
				// Keep isSubmitting and isRedirecting true while redirecting
			} else if (result.status === "needs_second_factor") {
				setNeedsTwoFactor(true);
				onTwoFactorStateChange?.(true);
				setIsSubmitting(false);
			} else {
				setGeneralError(t`Sign in incomplete. Please try again.`);
				setIsSubmitting(false);
			}
		} catch (err: unknown) {
			const clerkError = err as ClerkAPIResponseError;
			const errorCode = clerkError.errors?.[0]?.code;
			if (errorCode === "user_banned" || errorCode === "user_locked") {
				window.location.href = getLocalizedPathname("/locked-out");
				return;
			}

			const errorMessage =
				clerkError.longMessage ??
				clerkError?.errors?.[0]?.longMessage ??
				t`An error occurred during sign in.`;
			const errorParam = clerkError.errors?.[0]?.meta?.paramName;

			// Determine which field has the error
			if (errorParam === "identifier") {
				setEmailError(errorMessage);
			} else if (errorParam === "password") {
				setPasswordError(errorMessage);
			} else {
				setGeneralError(errorMessage);
			}
			setIsSubmitting(false);
		}
	};

	const signInWith = async (strategy: OAuthStrategy) => {
		if (!signIn) {
			return;
		}

		setIsSubmitting(true);
		setIsRedirecting(true);
		onRedirectStart?.();
		setEmailError("");
		setPasswordError("");
		setGeneralError("");

		try {
			// Cookies drive /auth/redirect role + is-new-user routing; they must land
			// before the OAuth navigation leaves the page. A fresh run also rewrites
			// the redirect-time cookie if its 1h maxAge lapsed while the tab idled.
			await refreshCookies();

			return signIn
				.authenticateWithRedirect({
					strategy,
					redirectUrl: getLocalizedPathname("/auth/sso-callback"),
					redirectUrlComplete:
						forceRedirectUrl ?? redirectUrl ?? getLocalizedPathname("/"),
					continueSignUp: true,
				})
				.catch((err) => {
					console.log(err);
					console.error(err, null, 2);
				});
		} catch (err: unknown) {
			const clerkError = err as ClerkAPIResponseError;
			setGeneralError(
				clerkError.longMessage ??
					clerkError?.errors?.[0]?.longMessage ??
					t`An error occurred during sign in.`,
			);
			setIsSubmitting(false);
			setIsRedirecting(false);
		}
	};
	// Paint the form from SSR; only the actions wait for Clerk. The redirect
	// spinner stays because it replaces the form after a successful submit.
	if (isRedirecting) {
		return (
			<div className="flex min-h-100 items-center justify-center">
				<Spinner />
			</div>
		);
	}

	if (needsTwoFactor && signIn) {
		return (
			<TwoFactorVerification
				signIn={signIn}
				setActive={setActive}
				forceRedirectUrl={forceRedirectUrl}
				redirectUrl={redirectUrl}
				onBack={() => {
					setNeedsTwoFactor(false);
					onTwoFactorStateChange?.(false);
				}}
				onRedirectStart={onRedirectStart}
			/>
		);
	}

	if (showForgotPassword) {
		return (
			<ForgotPassword
				initialEmail={emailAddress}
				onBack={() => setShowForgotPassword(false)}
				forceRedirectUrl={forceRedirectUrl}
				redirectUrl={redirectUrl}
				onRedirectStart={onRedirectStart}
				onTwoFactorStateChange={onTwoFactorStateChange}
			/>
		);
	}

	return (
		<div className="w-full max-w-md rounded-xl border bg-white p-7 shadow-custom lg:p-8">
			<div className="mb-5">
				<h1 className="font-bold text-2xl text-klarify-neutral-950 tracking-tight">
					<Trans>Sign in to your account</Trans>
				</h1>
				<p className="mt-1 text-[13px] text-muted-foreground">
					<Trans>to continue to Klarify</Trans>
				</p>
			</div>

			<Button
				type="button"
				onClick={() => signInWith("oauth_google")}
				disabled={isSubmitting || !isLoaded}
				aria-busy={!isLoaded}
				variant="outline"
				className="w-full gap-2"
			>
				<GoogleGIcon
					width={16}
					height={16}
				/>
				<Trans>Continue with Google</Trans>
			</Button>

			<div className="relative my-4">
				<div className="absolute inset-0 flex items-center">
					<div className="w-full border-klarify-gray-mod-200 border-t" />
				</div>
				<div className="relative flex justify-center text-sm">
					<span className="bg-white px-2 text-klarify-gray-mod-400">
						<Trans>or</Trans>
					</span>
				</div>
			</div>

			<form
				onSubmit={handleSubmit}
				className="space-y-4"
			>
				<div>
					<Label
						htmlFor="email"
						className="mb-1.5 block font-medium text-klarify-gray-mod-700"
					>
						<Trans>Email address</Trans>
					</Label>
					<Input
						type="email"
						id="email"
						name="email"
						autoComplete="email"
						value={emailAddress}
						onChange={(e) => setEmailAddress(e.target.value)}
						inputClassName={
							emailError
								? "border-destructive focus-visible:ring-destructive"
								: ""
						}
						required
					/>
					{emailError && (
						<p className="mt-1.5 text-destructive text-sm">{emailError}</p>
					)}
				</div>

				<div>
					<Label
						htmlFor="password"
						className="mb-1.5 block font-medium text-klarify-gray-mod-700"
					>
						<Trans>Password</Trans>
					</Label>
					<div className="relative">
						<Input
							type={showPassword ? "text" : "password"}
							id="password"
							name="password"
							autoComplete="current-password"
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							inputClassName={
								passwordError
									? "border-destructive pr-10 focus-visible:ring-destructive"
									: "pr-10"
							}
							required
						/>
						<Button
							type="button"
							variant="ghost"
							size="sm"
							onClick={() => setShowPassword(!showPassword)}
							className="absolute top-1/2 right-1 h-8 w-8 -translate-y-1/2 cursor-pointer p-0 text-klarify-gray-mod-500 hover:bg-transparent hover:text-klarify-gray-mod-700"
							aria-label={showPassword ? t`Hide password` : t`Show password`}
						>
							{showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
						</Button>
					</div>
					{passwordError && (
						<p className="mt-1.5 text-destructive text-sm">{passwordError}</p>
					)}
					<div className="mt-1 text-right">
						<Button
							type="button"
							variant="link"
							onClick={() => setShowForgotPassword(true)}
							className="h-auto p-0 text-primary text-sm hover:text-primary/90"
						>
							<Trans>Forgot password?</Trans>
						</Button>
					</div>
				</div>

				{generalError && (
					<div className="rounded-md bg-destructive/5 p-3 text-destructive text-sm">
						{generalError}
					</div>
				)}

				<Button
					type="submit"
					disabled={isSubmitting || !isLoaded}
					aria-busy={!isLoaded}
					className="mt-2 h-11 w-full font-semibold"
				>
					{isSubmitting ? (
						<Trans>Signing in...</Trans>
					) : (
						<Trans>Continue</Trans>
					)}
				</Button>

				<div className="mt-2 text-center text-klarify-gray-mod-600 text-sm">
					<Trans>Don't have an account?</Trans>{" "}
					<a
						href={signUpUrl}
						className="font-medium text-primary hover:text-primary/90"
					>
						<Trans>Sign up</Trans>
					</a>
				</div>
			</form>
		</div>
	);
}
